import { useCallback, useMemo } from 'react';
import { getSubscribedPlanUseCase } from '../use-cases/get-subscribed-plan.use-case';
import { subscribedPlanAtom } from '../states/subscribed-plan.atom';
import useQuery from '../../../../shared/hooks/useQuery';
import { SubscribedPlan } from '../typings';

export function useSubscribedPlan(autoFetch = true) {
  const {
    requestData: { data: subscribedPlan, loading, error },
    setData,
    fetchData,
  } = useQuery<SubscribedPlan>({
    queryFunction: getSubscribedPlanUseCase,
    requestAtom: subscribedPlanAtom,
    autoFetch,
  });

  const shouldMakePayment = useCallback(() => {
    return subscribedPlan?.canMakeNextPayment;
  }, [subscribedPlan]);

  const askToContactSupport = useMemo(() => {
    return subscribedPlan?.canMakeNextPayment ? subscribedPlan.function?.type : undefined;
  }, [subscribedPlan]);

  const verifySubscribedPlan = useCallback(async () => {
    if (typeof subscribedPlan?.canMakeNextPayment === 'boolean') {
      return subscribedPlan.canMakeNextPayment;
    }

    const response = await fetchData();
    const newSubscribedPlanData = response?.data;
    if (typeof newSubscribedPlanData?.canMakeNextPayment === 'boolean') {
      return newSubscribedPlanData.canMakeNextPayment;
    }

    return false;
  }, [subscribedPlan, fetchData]);

  const isAddonPresent = useCallback(
    (addonId?: string) => {
      if (!subscribedPlan?.addons || !addonId) {
        return false;
      }
      return (
        subscribedPlan?.addons?.some((addon) => addon.addonId === addonId && new Date(addon.endDate) > new Date()) ||
        false
      );
    },
    [subscribedPlan]
  );

  const isMediaHidden = loading || (subscribedPlan && subscribedPlan?.piiSettings?.mediaPolicy === 'REJECT');

  const isPIITextHidden = loading || (subscribedPlan && ['REJECT', 'MASK'].includes(subscribedPlan?.piiSettings?.textPolicy || ''));

  return {
    subscribedPlan: subscribedPlan,
    verifySubscribedPlan,
    loading,
    askToContactSupport,
    error,
    shouldMakePayment,
    isAddonPresent,
    setSubscribedPlan: setData,
    isMediaHidden,
    isPIITextHidden,
  };
}
